import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Bot, Sparkles, Target } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  SectionToolbar,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { CampaignCreateButton } from '@/features/campaigns/components/campaign-create-button';
import { useProjects } from '@/features/projects/use-projects';
import {
  useCampaigns,
  type CampaignListItem,
} from '@/features/campaigns/use-campaigns';
import type { Project } from '@seo/types';
import { relTime } from '@/lib/format';
import { useProjectFilterParam } from '@/lib/use-project-filter';
import { AutoCampaignPanel } from '@/components/auto-campaign/panel';
import { useRuntimeConfig } from '@/features/auto-campaign/use-config';

const PROJECT_FILTER_STORAGE = 'seo:campaigns-project';
const ALL_SENTINEL = '__all__';

export function CampaignsRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];
  const navigate = useNavigate();

  // Filter + tab live in the URL (?project=&tab=manual) — shareable, and the
  // Manual tab survives refresh instead of resetting to Auto-Pilot.
  const { projectId, setProjectId } = useProjectFilterParam({
    storageKey: PROJECT_FILTER_STORAGE,
    projects: availableProjects,
    projectsLoading: projects.isLoading,
  });
  const search = useSearch({ strict: false }) as { tab?: 'auto' | 'manual' };
  const tab: 'auto' | 'manual' = search.tab ?? 'auto';
  const setTab = (v: 'auto' | 'manual'): void => {
    void navigate({
      search: (prev) =>
        ({ ...(prev as object), tab: v === 'auto' ? undefined : v }) as never,
    });
  };

  const campaigns = useCampaigns(projectId || undefined);
  // Auto-pilot fires live in the "Run history" of the Auto-Pilot tab — mixing
  // them into the manual list double-listed every run and confused users.
  const manualRows = (campaigns.data?.items ?? []).filter((c) => !c.is_auto);

  const runtimeConfig = useRuntimeConfig();
  const autoCampaignAvailable = runtimeConfig.data?.auto_campaign_enabled === true;
  const selectedProject = availableProjects.find((p) => p.id === projectId);

  const manualSection = (
    <ManualCampaignsSection
      projects={availableProjects}
      projectId={projectId}
      rows={manualRows}
      isLoading={campaigns.isLoading}
    />
  );

  return (
    <Page>
      <PageHeader
        title="Campaigns"
        description={
          autoCampaignAvailable
            ? 'Hands-off SEO on auto-pilot, or one-off goal campaigns you launch yourself — per project.'
            : 'Describe a goal in plain English; the Orchestrator picks the strategy and runs the right specialist agents.'
        }
      />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project filter">
            {availableProjects.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>No projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select
                value={projectId || ALL_SENTINEL}
                onValueChange={(v) =>
                  setProjectId(v === ALL_SENTINEL ? '' : v)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_SENTINEL}>All projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        </Section>

        {autoCampaignAvailable ? (
          <Tabs
            value={tab}
            onValueChange={(v) => setTab(v as 'auto' | 'manual')}
          >
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1">
              <TabsTrigger
                value="auto"
                className="w-full flex-col items-start gap-0.5 whitespace-normal px-4 py-2 text-left"
              >
                <span className="flex items-center gap-1.5 font-semibold">
                  <Bot className="h-4 w-4" />
                  Auto-Pilot
                </span>
                <span className="hidden text-xs font-normal text-muted-foreground sm:block">
                  Hands-off SEO: audits, keywords, briefs &amp; off-page drafts
                  run on a schedule and land in your Telegram.
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="manual"
                className="w-full flex-col items-start gap-0.5 whitespace-normal px-4 py-2 text-left"
              >
                <span className="flex items-center gap-1.5 font-semibold">
                  <Target className="h-4 w-4" />
                  Manual campaigns
                </span>
                <span className="hidden text-xs font-normal text-muted-foreground sm:block">
                  One-off campaigns: describe a goal in plain English and the
                  orchestrator plans + runs it once.
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="auto">
              {selectedProject ? (
                <AutoCampaignPanel project={selectedProject} />
              ) : (
                <Section>
                  <EmptyState
                    icon={Bot}
                    title="Pick a project to see its auto-pilot"
                    description="Auto-pilot is set up per project. Choose one in the project filter on the left."
                    variant="inline"
                  />
                </Section>
              )}
            </TabsContent>

            <TabsContent value="manual">{manualSection}</TabsContent>
          </Tabs>
        ) : (
          manualSection
        )}
      </div>
    </Page>
  );
}

function ManualCampaignsSection({
  projects,
  projectId,
  rows,
  isLoading,
}: {
  projects: Project[];
  projectId: string;
  rows: CampaignListItem[];
  isLoading: boolean;
}): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Target className="h-4 w-4 text-muted-foreground" />
            Manual campaigns
          </SectionTitle>
          <SectionDescription>
            You describe a goal, the orchestrator plans a DAG of agents and
            runs it once.
          </SectionDescription>
        </div>
        <SectionToolbar>
          <CampaignCreateButton
            projects={projects}
            defaultProjectId={projectId}
            buttonProps={{ size: 'sm' }}
          />
        </SectionToolbar>
      </SectionHeader>

      {isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No manual campaigns yet"
          description="Describe a goal and the orchestrator will plan + execute the right agent mix."
          variant="inline"
          action={
            <CampaignCreateButton
              projects={projects}
              defaultProjectId={projectId}
              label="Run your first campaign"
              buttonProps={{ size: 'sm' }}
            />
          }
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((c) => (
            <li key={c.id}>
              <Link
                to="/campaigns/$campaignId"
                params={{ campaignId: c.id }}
                className="group flex items-start justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span className="font-mono text-xs text-muted-foreground">
                      camp-{c.id.slice(0, 8)}
                    </span>
                  </div>
                  <div className="mt-1 line-clamp-2 text-sm text-foreground group-hover:text-primary">
                    {c.goal}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {c.horizon_days
                      ? `${c.horizon_days}-day horizon · `
                      : ''}
                    created {relTime(c.created_at)}
                  </div>
                </div>
                <StatusPill status={c.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
